import React, { useState, useEffect } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Chip,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as EducationIcon,
  Upload as UploadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PhotoCamera as PhotoIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material';

interface Education {
  id: string;
  drivingSchool: string;
  programTitle: string;
  completedDate: string;
  hasDocument?: boolean;
  documentFileId?: number;
  documentFile?: {
    id: number;
    url: string;
    name: string;
    size: number;
  };
}

interface EducationProps {
  data: Education[];
  onChange: (data: Education[]) => void;
}

// Generate years from 1980 to current year + 1
const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear + 1 - i);

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const days = Array.from({ length: 31 }, (_, i) => i + 1);

interface EducationSchool {
  id: number;
  name: string;
  active: boolean;
}

const Education: React.FC<EducationProps> = ({ data, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState<Education | null>(null);
  const [formData, setFormData] = useState<Partial<Education>>({});
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [educationSchools, setEducationSchools] = useState<EducationSchool[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [selectedDay, setSelectedDay] = useState<number | ''>('');
  
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Load education schools from API
  const loadEducationSchools = async () => {
    try {
      setSchoolsLoading(true);
      // Make a direct API call to the code-tables/education-schools endpoint
      const response = await fetch(`${import.meta.env.VITE_SIMPLE_REST_URL}/code-tables/education-schools`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const schools = await response.json();
        setEducationSchools(schools);
      } else {
        console.error('Failed to load education schools');
        notify('Failed to load education schools', { type: 'error' });
      }
    } catch (error) {
      console.error('Error loading education schools:', error);
      notify('Error loading education schools', { type: 'error' });
    } finally {
      setSchoolsLoading(false);
    }
  };

  useEffect(() => {
    loadEducationSchools();
  }, []);

  const handleAdd = () => {
    setEditingEducation(null);
    setFormData({});
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setDialogOpen(true);
  };

  const handleEdit = (education: Education) => {
    setEditingEducation(education);
    setFormData(education);
    
    // Parse the completed date if it exists
    if (education.completedDate) {
      const date = new Date(education.completedDate);
      setSelectedYear(date.getFullYear());
      setSelectedMonth(date.getMonth() + 1);
      setSelectedDay(date.getDate());
    } else {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
    }
    
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    onChange(data.filter(education => education.id !== id));
  };

  const handleSave = () => {
    if (!formData.drivingSchool || !formData.programTitle) {
      return;
    }

    // Build the completed date from year/month/day
    let completedDate = '';
    if (selectedYear && selectedMonth && selectedDay) {
      const date = new Date(selectedYear, selectedMonth - 1, selectedDay);
      completedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    }

    const educationData: Education = {
      id: editingEducation?.id || Date.now().toString(),
      drivingSchool: formData.drivingSchool!,
      programTitle: formData.programTitle!,
      completedDate,
      hasDocument: formData.hasDocument,
      documentFileId: formData.documentFileId,
      documentFile: formData.documentFile,
    };

    if (editingEducation) {
      onChange(data.map(education => education.id === editingEducation.id ? educationData : education));
    } else {
      onChange([...data, educationData]);
    }

    setDialogOpen(false);
    setFormData({});
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setEditingEducation(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setFormData({});
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setEditingEducation(null);
  };

  const updateFormData = (field: keyof Education, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Document upload handlers
  const handleDocumentUpload = () => {
    setDocumentDialogOpen(true);
    setPreviewUrl(formData.documentFile?.url || '');
  };

  const handleDocumentDialogClose = () => {
    setDocumentDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(formData.documentFile?.url || '');
    setIsDragOver(false);
  };

  const validateFile = (file: File): boolean => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return;

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDocumentSave = () => {
    if (selectedFile) {
      // In a real implementation, you would upload the file to a server here
      // For now, we'll simulate saving the document info
      const mockFileData = {
        id: Date.now(),
        url: previewUrl,
        name: selectedFile.name,
        size: selectedFile.size,
      };

      updateFormData('documentFile', mockFileData);
      updateFormData('documentFileId', mockFileData.id);
    }
    handleDocumentDialogClose();
  };

  const handleRemoveDocument = () => {
    updateFormData('documentFile', undefined);
    updateFormData('documentFileId', undefined);
    updateFormData('hasDocument', false);
    handleDocumentDialogClose();
  };

  const isImage = (filename?: string) => {
    if (!filename) return false;
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };


  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Add your educational background and training programs
      </Typography>

      <Stack spacing={3} mt={3}>
        {/* Add Education Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            p: 2,
            borderStyle: 'dashed',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderStyle: 'dashed',
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            }
          }}
        >
          Add Education
        </Button>

        {/* Education Cards */}
        {data.map((education) => (
          <Card key={education.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={2} flex={1}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: '#f3e5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <EducationIcon sx={{ color: '#7b1fa2' }} />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {education.drivingSchool}
                    </Typography>
                    <Typography variant="body1" color="text.primary" gutterBottom>
                      {education.programTitle}
                    </Typography>
                    {education.completedDate && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Completed: {formatDate(education.completedDate)}
                      </Typography>
                    )}
                    {education.documentFile && (
                      <Stack direction="row" spacing={1} mt={1} alignItems="center">
                        <InsertDriveFileIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                        <Typography variant="caption" color="success.main">
                          Document uploaded
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <IconButton onClick={() => handleEdit(education)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(education.id)} 
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}

        {data.length === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 2, borderStyle: 'dashed' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <EducationIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No education added yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add your educational background and training programs
              </Typography>
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Add/Edit Education Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingEducation ? 'Edit Education' : 'Add Education'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Driving School</FormLabel>
                <Select
                  value={formData.drivingSchool || ''}
                  onChange={(e) => updateFormData('drivingSchool', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select School
                  </MenuItem>
                  {educationSchools.map((school) => (
                    <MenuItem key={school.id} value={school.name}>
                      {school.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Program Title</FormLabel>
                <TextField
                  value={formData.programTitle || ''}
                  onChange={(e) => updateFormData('programTitle', e.target.value)}
                  placeholder=""
                  fullWidth
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Completed Date</FormLabel>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value as number)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" disabled>Year</MenuItem>
                      {years.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value as number)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" disabled>Month</MenuItem>
                      {months.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    <Select
                      value={selectedDay}
                      onChange={(e) => setSelectedDay(e.target.value as number)}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="" disabled>Day</MenuItem>
                      {days.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>

            {/* Document Upload Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 2 }}>Do you have a education/training document (file)?</FormLabel>
                <RadioGroup
                  value={formData.hasDocument === true ? 'true' : formData.hasDocument === false ? 'false' : ''}
                  onChange={(e) => {
                    const hasDoc = e.target.value === 'true';
                    updateFormData('hasDocument', hasDoc);
                    if (!hasDoc) {
                      updateFormData('documentFile', undefined);
                      updateFormData('documentFileId', undefined);
                    }
                  }}
                  row
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>

                {formData.hasDocument === true && (
                  <Box>
                    <FormLabel sx={{ mb: 2, display: 'block' }}>Attach your education/training document</FormLabel>
                    <Button
                      variant="contained"
                      onClick={handleDocumentUpload}
                      startIcon={<UploadIcon />}
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' },
                        mb: 2
                      }}
                    >
                      {formData.documentFile ? 'Replace Education File' : 'Add Education File'}
                    </Button>
                    
                    {formData.documentFile && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        backgroundColor: '#f5f5f5'
                      }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <InsertDriveFileIcon sx={{ color: '#1976d2' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formData.documentFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={!formData.drivingSchool || !formData.programTitle}
            sx={{
              backgroundColor: '#212121',
              '&:hover': { backgroundColor: '#424242' }
            }}
          >
            {editingEducation ? 'Update' : 'Add Education'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog 
        open={documentDialogOpen} 
        onClose={handleDocumentDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Education Document
            <IconButton onClick={handleDocumentDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {previewUrl ? (
              isImage(selectedFile?.name || formData.documentFile?.name) ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Education document preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 300,
                    borderRadius: 2,
                    mb: 2,
                  }}
                />
              ) : (
                <Box sx={{ mb: 2, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <InsertDriveFileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                  <Typography variant="body1">
                    {selectedFile?.name || formData.documentFile?.name}
                  </Typography>
                </Box>
              )
            ) : (
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('education-file-input')?.click()}
                sx={{
                  width: '100%',
                  height: 200,
                  border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  backgroundColor: isDragOver ? '#f3f7ff' : '#f9f9f9',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f3f7ff',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <UploadIcon sx={{ fontSize: 48, mb: 1, color: isDragOver ? '#1976d2' : 'inherit' }} />
                  <Typography variant="body2" sx={{ color: isDragOver ? '#1976d2' : 'inherit' }}>
                    {isDragOver ? 'Drop file here' : 'Drag and drop file here or click to select'}
                  </Typography>
                </Box>
              </Box>
            )}
            
            <input
              accept="image/*,.pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="education-file-input"
              type="file"
              onChange={handleInputChange}
            />
            <label htmlFor="education-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<InsertDriveFileIcon />}
                sx={{ mr: 1 }}
              >
                Select File
              </Button>
            </label>
            
            {formData.documentFile && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveDocument}
                sx={{ ml: 1 }}
              >
                Remove Document
              </Button>
            )}
            
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Maximum file size: 10MB • Supported formats: Images, PDF, DOC, DOCX
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDocumentDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDocumentSave}
            disabled={!selectedFile && !formData.documentFile}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Education;